import {
  Box,
  Flex,
  Text,
  Input,
  Button,
  HStack,
  useColorModeValue,
  VStack,
  keyframes,
  Spinner,
  Heading
} from "@chakra-ui/react";
import React, { useState, useEffect, useRef } from "react";
import { currentUser } from "utils/CurrentUser";
import axios from "axios";
import { ENDPOINTS } from "utils/Constants";
import { notify } from "components/ToastNotification";
import { useParams } from "react-router-dom";
import { getSocket } from "utils/SocketService";
import { useSearchParams } from "react-router-dom";
import TypingResponseRenderer from "utils/TypingResponseRenderer";

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export default function Chat() {
  const { skillId } = useParams();
  const [messages, setMessages] = useState([]);
  const [latestMessageIndex, setLatestMessageIndex] = useState(null);
  const [newMessage, setNewMessage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [lessons, setLessons] = useState([]);
  const chatContainerRef = useRef(null);
  const [searchParams] = useSearchParams();
  const [skillData, setSkillData] = useState({
    name: '',
    level: ''
  })
  const numLessons = searchParams.get("numLessons");
  const currentLevel = searchParams.get("currentLevel");

  const cardBg = useColorModeValue("navy.50", "gray.700");
  const cardTextColor = useColorModeValue("black", "white");
  const chatBg = useColorModeValue("white", "gray.800");
  const messageTextColor = useColorModeValue("gray.600", "gray.300");
  const inputBg = useColorModeValue("gray.200", "gray.600");
  const userBubble = useColorModeValue('blue.100', '#7551ff');


  const [sendButtonIsLoading, setSendButtonIsLoading] = useState(false);

  const [socket, setSocket] = useState(null);

  useEffect(() => {

    const socketInstance = getSocket();
    setSocket(socketInstance);
    socketInstance.on("response", (data) => {
      setMessages((prevMessages) => {
        const updatedMessages = [...prevMessages, { user: data.role, text: data.content }];
        setLatestMessageIndex(updatedMessages.length - 1);
        setNewMessage(true);
        return updatedMessages;
      });

      setSendButtonIsLoading(false);
    });

    socketInstance.on("fullLessonName", data => {
      setSkillData(data)
    })

    socketInstance.on("lessonResponse", (data) => {
      setLessons(data)
    })

    socketInstance.on("error", (data) => {
      console.log(data);
      setLoading(false);
      setMessages([]);
      setLessons([]);
    })

    socketInstance.emit("startConvoForSkill", { token: currentUser.token, skillId: skillId, numberOfLessons: numLessons, currentLevel: currentLevel });

    const getMySkillProgress = async () => {
      try {
        const response = await axios.get(ENDPOINTS.GET_MY_PROGRESS + skillId + (currentLevel ? `?currentLevel=${currentLevel}` : ''), {
          headers: {
            Authorization: `Bearer ${currentUser.token}`,
          },
        });
        const responseData = response.data;
        if (!responseData.success) return notify(responseData.output);
        responseData.data?.progress?.forEach(data => {
          setMessages((prevMessages) => [
            ...prevMessages,
            { user: data.role, text: data.content },
          ]);
        })

        setLessons(responseData.data.lessons);
      } catch (error) {
        notify("Something wrong happened.", "error");
      }
    };

    getMySkillProgress();
    return () => {
      socketInstance.disconnect();
    };
  }, [currentUser.token])

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (newMessage) {
      const timer = setTimeout(() => setNewMessage(false), 2500);
      return () => clearTimeout(timer);
    }
  }, [newMessage]);

  useEffect(() => {
    if (lessons?.length > 0) {
      setLoading(false);
    } else if(messages?.length > 0) {
      socket?.emit("lessonRegenerate", { token: currentUser.token, skillId: skillId, numberOfLessons: numLessons, currentLevel: currentLevel })
    }
  }, [lessons]);

  const sendMessage = () => {
    if (input.trim()) {
      socket.emit("message", { user: "user", input, token: currentUser.token, skillId });
      setMessages((prevMessages) => [...prevMessages, { user: "user", text: input }]);
      setInput("");
      setSendButtonIsLoading(true);

      setNewMessage(false);
    }
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} px="20px">
      {loading && (
        <Flex
          position="fixed"
          top="0"
          left="0"
          w="100vw"
          h="100vh"
          bg="rgba(0, 0, 0, 0.7)"
          color="white"
          align="center"
          justify="center"
          zIndex="1000"
          animation="fade-in 1s"
          style={{ animation: loading ? "fade-in 1s" : "fade-out 1s" }}
        >
          <Box textAlign="center">
            <Spinner size="xl" mb="10px" />
            <Text fontSize="xl" fontWeight="bold">Junus is thinking...</Text>
          </Box>
        </Flex>
      )}
      <Flex
        p="20px"
        flexDirection={{ base: "column", md: "row" }}
        w="100%"
      >
        <Heading
          as="h2"
          size="md"
          color={cardTextColor}
          fontWeight="bold"
        >
          Currently learning: {skillData.name ?
            `${skillData.name} - ${skillData.level[0].toUpperCase() + skillData.level.slice(1)}`
            : ''}
        </Heading>
      </Flex>
      <Flex
        p="20px"
        flexDirection={{ base: "column", md: "row" }}
        w="100%"
        h={{ base: "auto", md: "600px" }}
      >
        {/* Lessons Section */}
        <Flex
          bg={chatBg}
          color={cardTextColor}
          mb={{ base: "20px", md: "0" }}
          borderRadius="10px"
          boxShadow="md"
          flexDirection="column"

          p="20px"
          w={{ base: "100%", md: "30%" }}
          h={{ base: "auto", md: "600px" }}
        >
          <Text fontWeight="bold" mb="10px">
            Lessons
          </Text>
          <VStack spacing="10px" align="start" width="100%" overflowY={{ base: "visible", md: "auto" }}>
            {lessons?.map((lesson, idx) => (
              <Box
                key={idx}
                p="12px"
                bg={cardBg}
                borderRadius="10px"
                boxShadow="md"
                cursor="pointer"
                _hover={{ transform: "scale(1.02)" }}
                transition="all 0.2s ease-in-out"
                width="100%"
              >
                {/* Lesson Title */}
                <Text fontWeight="bold" fontSize="md" mb="8px">
                  {idx + 1}. {lesson.name}
                </Text>

                {/* Topics Covered */}
                <VStack spacing="5px" align="start" pl="10px">
                  {lesson.data?.map((includedTopic, topicIdx) => (
                    <Flex key={topicIdx} align="center">
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        mr="6px"
                        color={messageTextColor}
                      >
                        {topicIdx + 1}.
                      </Text>
                      <Text fontSize="sm" color={messageTextColor}>
                        {includedTopic}
                      </Text>
                    </Flex>
                  ))}
                </VStack>
              </Box>
            ))}
          </VStack>
        </Flex>

        {/* Chat Section */}
        <Flex
          bg={chatBg}
          color={cardTextColor}
          p="20px"
          borderRadius="10px"
          boxShadow="md"
          flexDirection="column"
          w={{ base: "100%", md: "70%" }}
          h={{ base: "auto", md: "600px" }}
        >
          {/* Messages */}
          <Box
            ref={chatContainerRef}
            flex="1"
            overflowY="auto"
            mb="10px"
            p="10px"
            borderRadius="5px"
            bg={chatBg}
          >
            {messages.map((msg, idx) => (
              <Flex
                key={idx}
                mb="10px"
                justify={msg.user === "user" ? "flex-end" : "flex-start"}
              >
                <Box
                  bg={msg.user === "user" ? userBubble : cardBg}
                  color={cardTextColor}
                  p="10px"
                  borderRadius="10px"
                  maxWidth="100%"
                  animation={
                    idx === latestMessageIndex && msg.user !== "user"
                      ? `${fadeIn} 0.5s ease-in`
                      : "none"
                  }
                >
                  {msg.user === "user" ? (
                    <>
                      <Text fontSize="sm" color={cardTextColor}>
                        User
                      </Text>
                      <Text color={cardTextColor}>{msg.text}</Text>
                    </>
                  ) : (
                    <>
                      <Text fontSize="sm" color={messageTextColor}>
                        Junus
                      </Text>
                      <TypingResponseRenderer
                        fullResponse={msg.text}
                        isNewMessage={(idx === messages.length - 1) && newMessage}
                        onTyping={() => {
                          if (chatContainerRef.current) {
                            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
                          }
                        }}
                      />
                    </>
                  )}
                </Box>
              </Flex>
            ))}
          </Box>

          {/* Input Area */}
          <HStack>
            <Input
              placeholder="Type your message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  sendMessage();
                }
              }}
              bg={inputBg}
              color={cardTextColor}
            />
            <Button
              variant="darkBrand"
              onClick={sendMessage}
              isLoading={sendButtonIsLoading}
            >
              Send
            </Button>
          </HStack>
        </Flex>
      </Flex>
    </Box>
  );

}
